import { useCallback, useMemo, useRef } from 'react';

import { User } from '@adsk/acs-sc-web-platform-api';
import * as analytics from '@adsk/acs-web-analytics';
import { useLocale } from '@adsk/alloy-react-locale';

import { USER_ID_TYPE, ANALYTICS } from '../consts';
import { useDeletedUsers } from '../DeletedUsers';
import { useUsers } from '../UsersContext';

export type DisplayUserType = {
  displayName?: string;
  userDetails?: User;
};

export type FormattedUserReturnValue = {
  formatDisplayUser(
    userId: string,
    options?: FormattedDisplayUserOptions,
  ): DisplayUserType;
  loading: boolean;
  error: null | unknown;
};

export type FormattedDisplayUserOptions = {
  ignoreDeletedFormat?: boolean;
};

export type FormattedUserParams = {
  searchBy?: string;
};

const DEFAULT_FORMATTED_USER: FormattedUserParams = {
  searchBy: USER_ID_TYPE.AUTODESK_ID,
};

export const useFormattedUser = ({
  searchBy = USER_ID_TYPE.AUTODESK_ID,
}: FormattedUserParams = DEFAULT_FORMATTED_USER): FormattedUserReturnValue => {
  const { T } = useLocale(({ locale }) => require(`./../i18n/${locale}.json`));

  const { data: users, loading: usersLoading, error: usersError } = useUsers();

  const {
    data: deletedUsers,
    loading: deletedUsersLoading,
    error: deletedUsersError,
  } = useDeletedUsers();

  const usersById: Map<string, User> = useMemo(() => {
    const allUsers: User[] = users.concat(deletedUsers);
    return new Map<string, User>(
      allUsers.map((user) =>
        searchBy === USER_ID_TYPE.AUTODESK_ID
          ? [user.autodeskId, user]
          : [user.id, user],
      ),
    );
  }, [users, deletedUsers, searchBy]);

  const userNotFoundAnalytics = useRef(new Set<string>());

  const formatDisplayUser = useCallback(
    (
      userId: string,
      { ignoreDeletedFormat = false }: FormattedDisplayUserOptions = {},
    ): DisplayUserType => {
      const currentUser = usersById.get(userId);

      if (
        !currentUser &&
        (deletedUsersLoading || usersLoading || usersError || deletedUsersError)
      ) {
        return { displayName: undefined };
      }

      if (currentUser) {
        if (!ignoreDeletedFormat && currentUser.status === 'deleted') {
          return {
            userDetails: currentUser,
            displayName: T.translate('REMOVED_USER_FORMAT', {
              username: currentUser.name,
            })?.toString(),
          };
        }

        return {
          userDetails: currentUser,
          displayName: currentUser.name,
        };
      }

      if (!userNotFoundAnalytics.current.has(userId)) {
        const errorMessage = `Tried to format user - ${userId} was searched by ${searchBy} and was not found in users list`;

        analytics.track(ANALYTICS.FORMAT_USER_ID_NOT_FOUND, {
          user_id: userId,
          user_id_type: searchBy,
          message: errorMessage,
        });
        console.error(errorMessage);
        userNotFoundAnalytics.current.add(userId);
      }

      return { displayName: undefined };
    },
    [
      usersById,
      deletedUsersLoading,
      usersLoading,
      usersError,
      deletedUsersError,
      searchBy,
      T,
    ],
  );

  return {
    formatDisplayUser,
    loading: usersLoading || deletedUsersLoading,
    error: usersError || deletedUsersError,
  };
};
